import React, { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useDeleteElementDrawer } from "../../hooks/useDesignElementDrawer";
import {
  QBackLink,
  QBodyLayout,
  QButton,
  QButtonGroup,
  QDateMetadataItem,
  QHeader,
  QHeading,
  QHorizontalMetadata,
  QNotFound,
  QOpenPropertiesPanelButton,
  QPageLoader,
  QPropertiesPanel,
  QStack,
  QText,
  QTitle,
  QTogglePropertiesPanelButton,
} from "@qualio/ui-components";
import { isDesignElementTestCase } from "@design-controls/types";
import DOMPurify from "dompurify";
import { DesignReviewOverviewTable } from "../../components/DesignReviewOverviewTable/DesignReviewOverviewTable";
import { RequirementTable } from "../../components/OverviewTable/RequirementTable";
import { RisksTable } from "../../components/OverviewTable/RisksTable";
import { AttachmentTable } from "../../components/OverviewTable/AttachmentTable";
import { isSoureEditableAndDeletable } from "../../domain/editableSource";
import { Outlet, useNavigate } from "react-router";
import { useCurrentProduct } from "../../hooks/useCurrentProduct";
import { TestResultTable } from "../../components/OverviewTable/TestResultTable";
import { GapAnalysis } from "../../components/GapAnalysis/GapAnalysis";
import { sourceDisplayName } from "../../constants/sourceDisplayName";
import { DesignElementStatus } from "../../components/DesignElementStatus/DesignElementStatus";
import useHasPermission from "../../hooks/useHasPermission";
import { UserPermissions } from "../../domain/permissions";
import { useCurrentConfigs } from "../../hooks/useCurrentConfigs";

export const TestCaseOverview: React.FC = () => {
  const { t } = useTranslation();
  const { isLoading, designElement, designElementHistory, version } =
    useDeleteElementDrawer();
  const navigate = useNavigate();
  const { name } = useCurrentProduct();
  const permissions = useHasPermission([UserPermissions.EDIT_DESIGN_ELEMENTS]);
  const configs = useCurrentConfigs();

  const requirements = useMemo(() => {
    if (!designElement) {
      return [];
    }

    return [
      ...((designElement as any).children ?? []),
      ...((designElement as any).parent ?? []),
    ].filter((element) => element.type.startsWith("req"));
  }, [designElement]);

  const risks = useMemo(() => {
    if (!designElement) {
      return [];
    }

    const items = [
      ...(designElement as any).sourcedItems,
      ...((designElement as any).mitigates ?? []),
    ];

    return Array.from(new Map(items.map((item) => [item.id, item])).values());
  }, [designElement]);

  const onEditClick = useCallback(() => {
    navigate("edit");
  }, [navigate]);

  if (isLoading) {
    return <QPageLoader />;
  }

  if (!designElement || !isDesignElementTestCase(designElement)) {
    return <QNotFound />;
  }

  return (
    <QBodyLayout.Default>
      <QHeader>
        <QBackLink href="../test-case" data-cy="back-btn">
          {t("products.backLink", { productName: name })}
        </QBackLink>
        <QTitle>
          <QText as="span" color="gray.500">
            {designElement?.code}
          </QText>{" "}
          {designElement?.title}
        </QTitle>
        <QHorizontalMetadata>
          <DesignElementStatus {...designElement} />
          <QDateMetadataItem
            label={t("common.metadata.created")}
            value={new Date(designElement?.created ?? 0)}
          />
          <QDateMetadataItem
            label={t("common.metadata.lastModified")}
            value={new Date(designElement?.updated ?? 0)}
          />
        </QHorizontalMetadata>
        <QButtonGroup>
          {isSoureEditableAndDeletable(designElement.source) &&
            permissions[UserPermissions.EDIT_DESIGN_ELEMENTS] &&
            version === "v0" && (
              <QButton
                leftIcon="Edit3"
                variant="outline"
                data-cy="edit"
                onClick={onEditClick}
              >
                {t("common.actions.edit")}
              </QButton>
            )}
          {!isSoureEditableAndDeletable(designElement.source) && (
            <a
              href={designElement.url}
              rel="noopener noreferrer"
              target="_blank"
            >
              <QButton
                data-cy="integration-button"
                variant="outline"
                rightIcon="ExternalLink"
              >
                {sourceDisplayName(designElement.source)}
              </QButton>
            </a>
          )}
          <QOpenPropertiesPanelButton />
        </QButtonGroup>
        <GapAnalysis
          policyIssues={designElement.policyIssues}
          type={designElement.type}
        />
      </QHeader>
      <QStack gap="24px" data-cy="test-case-overview">
        <QStack gap="8px" className="ck-content document">
          <QText fontSize="sm" fontWeight={600} color="gray.900">
            {t("testCases.overview.sections.description")}
          </QText>
          {designElement.description && (
            <QText
              fontSize={14}
              style={{ wordBreak: "break-word" }}
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(designElement.description),
              }}
            />
          )}
        </QStack>

        <TestResultTable result={designElement.testResult} />

        {designElementHistory && designElementHistory?.length > 0 && (
          <DesignReviewOverviewTable changeHistory={designElementHistory} />
        )}

        {!!requirements.length ||
          (!!risks.length && (
            <QStack gap="8px">
              <QHeading fontSize="md" fontWeight={600}>
                {t("testCases.overview.sections.relatedDesignElements")}
              </QHeading>
              <QText fontSize="sm">
                {t("testCases.overview.relatedDescription")}
              </QText>
            </QStack>
          ))}

        <RequirementTable requirements={requirements} />
        <RisksTable risks={risks} />

        {designElement.attachments.length > 0 && (
          <>
            <QStack gap="8px">
              <QHeading fontSize="md" fontWeight={600}>
                {t("testCases.overview.sections.attachments")}
              </QHeading>
              <QText fontSize="sm">
                {t("testCases.overview.attachmentsDescription")}
              </QText>
            </QStack>
            <AttachmentTable attachments={designElement.attachments} />
          </>
        )}

        <QPropertiesPanel openOnMount>
          <QStack spacing="8px" w="300px">
            <QStack direction="row" justifyContent="space-between">
              <QTitle>{t("testCases.overview.properties.title")}</QTitle>
              <QTogglePropertiesPanelButton />
            </QStack>
            <QStack>
              <QText fontSize="sm" fontWeight={600}>
                {t("testCases.overview.properties.type")}
              </QText>
              <QText fontSize="sm" data-cy="type">
                {
                  (
                    configs.find((c) => c.type === designElement.type) as
                      | { label: string }
                      | undefined
                  )?.label
                }
              </QText>
            </QStack>
            {designElement.category && (
              <QStack>
                <QText fontSize="sm" fontWeight={600}>
                  {t("testCases.overview.properties.category")}
                </QText>
                <QText fontSize="sm" data-cy="category">
                  {designElement.category?.label}
                </QText>
              </QStack>
            )}
          </QStack>
        </QPropertiesPanel>
      </QStack>
      <Outlet />
    </QBodyLayout.Default>
  );
};
